import { Logo } from "@/components/logo";
import { ThemeToggle } from "@/components/theme-toggle";
import { useSignOut } from "@/hooks/use-sign-out";
import { getCdnUrl } from "@papier/shared/utils/cdn-utils";
import { firstCharacter } from "@papier/shared";
import { useTheme } from "@papier/ui/components/theme-provider";
import { Avatar, AvatarFallback, AvatarImage } from "@papier/ui/components/ui/avatar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@papier/ui/components/ui/breadcrumb";
import { Separator } from "@papier/ui/components/ui/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from "@papier/ui/components/ui/sidebar";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  linkOptions,
  Outlet,
  redirect,
  useLocation,
} from "@tanstack/react-router";
import { Building2, Home, LogOut, Monitor, Moon, Sun, Users } from "lucide-react";
import { Fragment } from "react";

export const Route = createFileRoute("/admin")({
  component: AdminRoute,
  beforeLoad: async ({ context: { queryClient, authOptions } }) => {
    const session = await queryClient.fetchQuery(authOptions.session);

    if (!session) {
      throw redirect({
        to: "/auth/$authView",
        params: { authView: "sign-in" },
      });
    }

    if (session.user.role !== "admin") {
      throw redirect({ to: "/" });
    }
  },
});

const sidebarItems = linkOptions([
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/organizations", label: "Organizations", icon: Building2 },
]);

function AdminRoute() {
  const { authOptions } = Route.useRouteContext();
  const location = useLocation({ select: (loc) => ({ pathname: loc.pathname }) });
  const { data: session } = useSuspenseQuery(authOptions.session);
  const { theme } = useTheme();
  const handleSignOut = useSignOut();

  if (!session) return null;

  const pathSegments = location.pathname.split("/").filter(Boolean);
  const relevantSegments = pathSegments[0] === "admin" ? pathSegments.slice(1) : pathSegments;

  return (
    <SidebarProvider>
      <Sidebar collapsible="icon">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild>
                <Link to="/admin">
                  <Logo />
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {sidebarItems.map((item) => (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton
                      asChild
                      tooltip={item.label}
                      isActive={location.pathname === item.to}
                    >
                      <Link to={item.to}>
                        <item.icon />
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <div className="group-data-[collapsible=icon]:hidden">
                <ThemeToggle />
              </div>
              <div className="hidden h-8 p-2 group-data-[collapsible=icon]:block">
                {theme === "light" ? (
                  <Sun className="size-4" />
                ) : theme === "dark" ? (
                  <Moon className="size-4" />
                ) : (
                  <Monitor className="size-4" />
                )}
              </div>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton asChild>
                <Link to="/">
                  <Home />
                  <span>Home</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Logout" onClick={handleSignOut}>
                <LogOut className="size-4" />
                <span>Logout</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" className="hover:bg-transparent!">
                <Avatar className="size-8">
                  <AvatarImage src={getCdnUrl(session.user.image)} alt={session.user.name} />
                  <AvatarFallback className="rounded-lg uppercase">
                    {firstCharacter(session.user.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{session.user.name}</span>
                  <span className="truncate text-xs">{session.user.email}</span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <SidebarInset className="overflow-hidden">
        <header className="flex h-12 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/admin">Admin</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {relevantSegments.length > 0 && <BreadcrumbSeparator />}
                {relevantSegments.map((segment, index) => {
                  const to = `/admin/${relevantSegments.slice(0, index + 1).join("/")}`;
                  const isLast = index === relevantSegments.length - 1;
                  return (
                    <Fragment key={to}>
                      <BreadcrumbItem>
                        {isLast ? (
                          <BreadcrumbPage className="capitalize">{segment}</BreadcrumbPage>
                        ) : (
                          <BreadcrumbLink asChild className="capitalize">
                            <Link to={to}>{segment}</Link>
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>
                      {!isLast && <BreadcrumbSeparator />}
                    </Fragment>
                  );
                })}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>
        <div className="px-4 pb-4">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
